import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { GiftIcon, TagIcon, WrenchIcon, ShirtIcon, CakeIcon, CheckCircleIcon } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useAuth } from '../../lib/AuthContext';
import { MOCK_REWARDS } from '../../lib/mockData';
import type { Reward } from '../../lib/types';
export function RewardsPage() {
  const {
    currentCustomer,
    isCustomerLoggedIn
  } = useAuth();
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  if (!isCustomerLoggedIn || !currentCustomer) {
    return <div className="max-w-4xl mx-auto px-4 py-16 text-center" data-id="element-1582">
        <GiftIcon className="w-16 h-16 text-[#666666] mx-auto mb-4" data-id="element-1583" />
        <h2 className="text-xl font-semibold text-white mb-2" data-id="element-1584">
          Vui lòng đăng nhập
        </h2>
      </div>;
  }
  const getCategoryIcon = (cat: string) => {
    switch (cat) {
      case 'voucher':
        return <TagIcon className="w-6 h-6" data-id="element-1585" />;
      case 'service':
        return <WrenchIcon className="w-6 h-6" data-id="element-1586" />;
      case 'merchandise':
        return <ShirtIcon className="w-6 h-6" data-id="element-1587" />;
      case 'birthday':
        return <CakeIcon className="w-6 h-6" data-id="element-1588" />;
      default:
        return <GiftIcon className="w-6 h-6" data-id="element-1589" />;
    }
  };
  const handleRedeem = () => {
    setSelectedReward(null);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };
  const availableRewards = MOCK_REWARDS.filter(r => r.isAvailable && r.pointsCost > 0);
  return <div className="max-w-4xl mx-auto px-4 py-8" data-id="element-1590">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-1591">
        <div className="flex items-center justify-between mb-6" data-id="element-1592">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-id="element-1593">
            <GiftIcon className="w-7 h-7 text-[#C8A84E]" data-id="element-1594" />
            Phần Thưởng
          </h1>
          <Badge variant="gold" size="md" data-id="element-1595">
            {currentCustomer.points.toLocaleString('vi-VN')} điểm
          </Badge>
        </div>
      </motion.div>

      {showSuccess && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-4 bg-[#22C55E]/10 border border-[#22C55E]/20 rounded-xl p-4 flex items-center gap-3" data-id="element-1596">
          <CheckCircleIcon className="w-5 h-5 text-[#22C55E]" data-id="element-1597" />
          <span className="text-sm text-[#22C55E]" data-id="element-1598">Đổi thưởng thành công!</span>
        </motion.div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1599">
        {availableRewards.map((reward, i) => {
        const canAfford = currentCustomer.points >= reward.pointsCost;
        return <motion.div key={reward.id} initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.05
        }} data-id="element-1600">
              <Card className={`h-full flex flex-col ${!canAfford ? 'opacity-60' : ''}`} data-id="element-1601">
                <div className="w-12 h-12 rounded-xl bg-[#C8A84E]/10 flex items-center justify-center text-[#C8A84E] mb-3" data-id="element-1602">
                  {getCategoryIcon(reward.category)}
                </div>
                <h3 className="text-base font-semibold text-white mb-1" data-id="element-1603">
                  {reward.nameVi}
                </h3>
                <p className="text-sm text-[#B0B0B0] mb-4 flex-1" data-id="element-1604">
                  {reward.description}
                </p>
                <div className="flex items-center justify-between" data-id="element-1605">
                  <span className="text-lg font-bold text-[#C8A84E]" data-id="element-1606">
                    {reward.pointsCost} điểm
                  </span>
                  <Button size="sm" variant={canAfford ? 'primary' : 'ghost'} disabled={!canAfford} onClick={() => setSelectedReward(reward)} data-id="element-1607">
                    {canAfford ? 'Đổi' : 'Chưa đủ'}
                  </Button>
                </div>
                {reward.stock <= 10 && <p className="text-xs text-[#F59E0B] mt-2" data-id="element-1608">
                    Còn {reward.stock} phần
                  </p>}
              </Card>
            </motion.div>;
      })}
      </div>

      <Modal isOpen={!!selectedReward} onClose={() => setSelectedReward(null)} title="Xác Nhận Đổi Thưởng" data-id="element-1609">
        {selectedReward && <div className="space-y-4" data-id="element-1610">
            <div className="bg-[#141414] rounded-lg p-4" data-id="element-1611">
              <h3 className="font-semibold text-white mb-1" data-id="element-1612">
                {selectedReward.nameVi}
              </h3>
              <p className="text-sm text-[#B0B0B0]" data-id="element-1613">
                {selectedReward.description}
              </p>
            </div>
            <div className="flex justify-between text-sm" data-id="element-1614">
              <span className="text-[#B0B0B0]" data-id="element-1615">Chi phí</span>
              <span className="text-[#C8A84E] font-semibold" data-id="element-1616">
                {selectedReward.pointsCost} điểm
              </span>
            </div>
            <div className="flex justify-between text-sm" data-id="element-1617">
              <span className="text-[#B0B0B0]" data-id="element-1618">Điểm hiện tại</span>
              <span className="text-white" data-id="element-1619">
                {currentCustomer.points.toLocaleString('vi-VN')} điểm
              </span>
            </div>
            <div className="flex justify-between text-sm border-t border-[#2A2A2A] pt-2" data-id="element-1620">
              <span className="text-[#B0B0B0]" data-id="element-1621">Điểm còn lại</span>
              <span className="text-white font-semibold" data-id="element-1622">
                {(currentCustomer.points - selectedReward.pointsCost).toLocaleString('vi-VN')}{' '}
                điểm
              </span>
            </div>
            <div className="flex gap-3" data-id="element-1623">
              <Button variant="ghost" fullWidth onClick={() => setSelectedReward(null)} data-id="element-1624">
                Hủy
              </Button>
              <Button fullWidth onClick={handleRedeem} data-id="element-1625">
                Xác Nhận Đổi
              </Button>
            </div>
          </div>}
      </Modal>
    </div>;
}